import React from 'react';
import WName from '../../../WName';
import PL from '../../../problems/ProblemLink';
import QL from '../../../faq/QuestionLink';
import AL from '../../../angles/AngleLink';
import NW from '../../../neologisms/NewWord';
import OML from '../../../ormaybe/OrMaybeLink';
import LRL from '../../LongReadLink';
import {E1, E2, Qual} from '../../../common';

import {godel} from './falsification/godel';
import {tarski} from './falsification/tarski';
import {russell} from './falsification/russell';
import {curry} from './falsification/curry';
import {induction} from './falsification/induction';

// ‘’ … –

export const falsification = {
  name: "falsification",
  alternatives: "false,falsify,falsifies,falsified",
  prelude: <span>Frankly, you cannot <PL name="denial">honestly</PL> refuse to <i><LRL name="belief">accept</LRL></i> the ‘<LRL>nepantla</LRL>’ proof. But if you use <i>that</i> as a basis (instead of <PL>ZFC</PL>)…</span>,
  header: <span>
    Toppling the house of cards
  </span>,
  outside: <span>
    The logic of containment is ending.
  </span>,
  preview: <div className="long-read-preview">
    <p>The <QL>Weltanschauung</QL> of apartness (<NW>apartheid</NW>) is called ‘<NW>atomocentrism</NW>’. It has been (<PL name="form">formally</PL>) falsified, as repeated here:</p>
    <ul>
      <li>Truth does not supervene on force/might or majority of <AL name="handicap">hominid</AL> <PL name="seb">bodies</PL> (‘<PL>democracy</PL>’), or even <i>consensus</i>. It emerges (at this ‘<NW>level</NW>’) from <i><AL name="fields.">logic</AL></i>.</li>
      <li>If there is a true (<OML name="soul">fundamental</OML>) <i>separation</i> in consciousness – other than what is apparent as emergent from (and supervening on) the physical particulars that allow <QL>consciousness</QL> to <PL>manifest</PL> at this ‘<NW>level</NW>’ – then either:</li>
      <ul>
        <li>You <LRL name="belief">seriously believe</LRL> that we’re all in a <OML>story</OML>, and our author is not <i>letting</i> us <LRL name="communism">share thoughts</LRL>, or</li>
        <li>By the ‘<LRL name="nepantla.">Pratty contrapositive</LRL>’, if <NW>GodEE</NW><sup>™</sup> ‘<LRL name="i.">exists</LRL>’ <i>at our ‘<NW>level</NW>’</i> then He<sup>™</sup> would be discoverable. Find Him<sup>™</sup>.</li>
      </ul>
      <li>Until then, we’re going with Mr. <NW>Occam</NW>.</li>
    </ul>
  </div>,
  text: <div className="long-read-text">
    <p>Let’s instead use an actually-‘<QL>consistent</QL>’ <AL name="fields.">logic</AL>. Using <PL name="ch.">Amara</PL>’s <QL name="consistent.ya">я-consistency</QL> as a standard insteaad of just the <PL>human-standard</PL>’s ω (omega) variety.</p>
    <p>The nepantla proof disproves a bunch of Atomland’s pillars holding up the <PL>ignorabimus</PL> (some things, son, you will <i>never know</i>). But you do not attain <AL name="religion.jain.kevala">केवल ज्ञान</AL> (kevala jñāna: <AL name="fields.logic.buffalo">supreme</AL> comprehension) by accumulating <i>more</i>. You reach that (and ‘<NW>enlightenment</NW>’) by <i><AL name="loss">letting go</AL></i>. केवल ज्ञान is a <AL name="sleeping">latent</AL> potential in any <NW>POCA</NW> (<QL name="languages">जीव/jīva</QL>), and you attain it by shedding the certainty that <AL>you</AL> or some ‘<QL>other</QL>’ could ever know <i>all</i>.</p>
    <p>Now let’s go through a handful of ‘Western’ <LRL>notions</LRL> built on the <AL name="fields.">logic</AL> of containment (<PL>hierarchy</PL> and <PL>exclusivity</PL>) and take them down.</p>
  </div>,

  sublist: [
    godel,
    tarski,
    russell,
    curry,
    induction,
  ],
};
  

/*

Anything that proves the ‘inexpressibility theorem’ (by falsifying the formula for я-consistency) is inconsistent. 
And the nepantla proof falsifies blanket negation because it allows you to construct e-alpha even not knowing all of the particulars; if you allow blanket negation, you can say: ‘There is not anything left out.’ Creating P ∧ ¬P (P and not-P or, as <PL name="ch.">Uwa</PL> would <QL name="silly">say</QL>, ‘P mountain <i>wham!</i>-symbol P’): a contradiction.</p>
    <p>So what about the <NW name="conservatism">conservatively-thinking</NW> claim (or complaint) that then <i>inaction<i> shouldn’t t be seen as an <PL>evil</PL>? That ties into the arrow of <QL>time</QL> and <AL name="notnew.p.heidegger.">Dasein</AL>.
    <p>Inaction is not <LRL name="nihilism">absence</LRL>. It is succumbing (cf. <NW word="overcome">Ganzesystemüberwindung</NW>) to <PL>reactivity</PL>: the <AL name="notnew.p.arendt.">banality of evil</AL>.</p>
  </div>,
  </div>,
};

You can use IBK logic to build the set T (of all ‘true’ statements).
(Falsifying the Polm Alfred Tarski)
<p style={{marginLeft: "10%"}}><b>Tarski’s ‘undefineability theorem’</b>: There is no <i>L</i>-formula True(n) such that for every <i>L</i>-sentence A, True(<i>g</i>(A) )  ⟺ A holds in <b>N</b>.</p>
<p>‘Well, there <i>is</i>,’ Amara said. ‘That’s what “true” means when you define truth as <i>emergent</i> and not “<PL name="metaphysics">metaphysically</PL>” imposed by some OutsideArbitrer, cf. the <NW>FOE</NW>.’</p>
<p>‘<QL name="pratty.">Atomland</QL>’s issue with this,’ <PL name="ch.">Ishmael said, ‘is that Atomlanders seriously <i><LRL>believe</LRL></i> in the possibility of in-<PL name="ick.">system</PL> “omniscience”. Not <AL name="religion.jain.kevala">केवल ज्ञान</AL> as it actually is, but the <i><NW word="illusions">magical</NW></i> kind. That solves all YOUR problems without you have=ing to <i>do any work</i> – or, you know, <i>share</i>, most crucially, your <PL>private</PL> <i><LRL name="communism">thoughts</LRL></i>, with any “<QL>other</QL>”.’

The disporrf of the traversability of the set T: for any two statements <i>a</i> and <i>b</i> in T, you can make a new statement <i>a</i> ∧ <i>b</i> also in T.

You can also make a bunch like <i>a</i> ∨ 0, <i>a</i> ∨ 1, <i>a</i> ∨ 2, <i>a</i> ∨ </i>n</i> for any (∀: the headstand symbol) <i>n</i> trivially proving that the set T has a LUB of the cardinality of <b>N</b>.


So it’s like the set of “all” prime numbers,’
*/
